import { useCallback, useEffect, useState } from "react";

import localStorageUtils from "~/helpers/localStorageUtils";
import useSaveLocalPreference from "~/hooks/useSaveLocalPreference";

export type ScannerModule = "material" | "availability";

const scannerPreferenceChangeEvent = "worksuite:scanner-preference-change";

const storageKey = (module: ScannerModule): string => `scanner.${module}`;

const readScannerPreference = (module: ScannerModule): boolean =>
	localStorageUtils.readItem(storageKey(module), "false") === "true";

const useScannerPreference = (module: ScannerModule): [boolean, (enabled: boolean) => void] => {
	const saveLocalPreference = useSaveLocalPreference();
	const [enabled, setEnabled] = useState(() => readScannerPreference(module));

	useEffect(() => {
		const sync = () => setEnabled(readScannerPreference(module));
		sync();
		window.addEventListener(scannerPreferenceChangeEvent, sync);
		window.addEventListener("storage", sync);
		return () => {
			window.removeEventListener(scannerPreferenceChangeEvent, sync);
			window.removeEventListener("storage", sync);
		};
	}, [module]);

	const setScannerPreference = useCallback(
		(value: boolean) => {
			if (!saveLocalPreference(() => localStorage.setItem(storageKey(module), String(value)))) return;
			window.dispatchEvent(new Event(scannerPreferenceChangeEvent));
		},
		[module, saveLocalPreference]
	);

	return [enabled, setScannerPreference];
};

export default useScannerPreference;
